import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Users, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { format } from 'date-fns';

// Generate consistent color based on name
const getAvatarColor = (name) => {
  const colors = [
    'bg-brand-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-cyan-500',
    'bg-brand-600',
    'bg-pink-500',
    'bg-teal-500',
    'bg-orange-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Get initials from name
const getInitials = (firstName, lastName, username) => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return '?';
};

const PeopleList = ({
  collapsed,
  toggleCollapse,
  selectedTaskForPeopleFilter,
  getAvailablePeopleForTask,
  projectPeople,
  selectedProject,
  selectedDate,
  getPersonUtilization,
  allProjectAssignments = {},
  allPeople = [],
}) => {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-steel-200 relative ${collapsed ? 'cursor-pointer hover:bg-steel-50' : ''}`}
      onClick={collapsed ? toggleCollapse : undefined}
    >
      <div
        className={`p-3 border-b border-steel-200 cursor-pointer hover:bg-steel-50 transition-colors ${collapsed ? 'border-b-0' : ''}`}
        onClick={(e) => { e.stopPropagation(); toggleCollapse(); }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900 flex items-center">
              {collapsed ? (
                <Users className="w-5 h-5 text-slate-600" />
              ) : (
                selectedTaskForPeopleFilter
                  ? `Available People (${getAvailablePeopleForTask(selectedTaskForPeopleFilter).length})`
                  : `Project People (${projectPeople.length})`
              )}
            </h2>
            {!collapsed && (
              <p className="text-xs text-slate-600 mt-1">
                {selectedTaskForPeopleFilter
                  ? `Available for ${selectedTaskForPeopleFilter.subcategory} (${selectedTaskForPeopleFilter.startTime}-${selectedTaskForPeopleFilter.endTime})`
                  : `People assigned to this project on ${format(selectedDate, 'MMM d')}`
                }
              </p>
            )}
          </div>
          <div className="p-1 text-slate-500">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="p-3">
          {(() => {
          const displayPeople = selectedTaskForPeopleFilter
            ? getAvailablePeopleForTask(selectedTaskForPeopleFilter)
            : projectPeople;

          return displayPeople.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <p>{selectedTaskForPeopleFilter ? 'No people available' : 'No people assigned'}</p>
              <p className="text-xs text-slate-400 mt-1">
                {selectedTaskForPeopleFilter
                  ? 'All people have conflicting assignments'
                  : selectedProject
                  ? 'No assignments for this date'
                  : 'Select a project first'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
            {displayPeople.map(person => {
              const utilization = getPersonUtilization(person.id);
              const fullName = person.first_name && person.last_name
                ? `${person.last_name} ${person.first_name}`
                : person.username || 'User';
              const initials = getInitials(person.first_name, person.last_name, person.username);
              const avatarColor = getAvatarColor(fullName);

              let bgColorClass = '';
              let borderColorClass = 'border-steel-200';

              if (selectedTaskForPeopleFilter) {
                bgColorClass = 'bg-brand-50';
                borderColorClass = 'border-brand-200';
              } else {
                if (utilization === 100) {
                  bgColorClass = 'bg-green-100';
                  borderColorClass = 'border-green-300';
                } else if (utilization >= 76) {
                  bgColorClass = 'bg-red-50';
                  borderColorClass = 'border-red-200';
                } else if (utilization >= 51) {
                  bgColorClass = 'bg-orange-50';
                  borderColorClass = 'border-orange-200';
                } else if (utilization >= 26) {
                  bgColorClass = 'bg-yellow-50';
                  borderColorClass = 'border-yellow-200';
                } else {
                  bgColorClass = 'bg-white';
                  borderColorClass = 'border-steel-200';
                }
              }

              return (
                <div
                  key={person.id}
                  className={`border rounded-xl p-2 hover:shadow-md transition-all cursor-move ${bgColorClass} ${borderColorClass}`}
                  draggable={!!selectedTaskForPeopleFilter}
                  onDragStart={(e) => {
                    if (selectedTaskForPeopleFilter) {
                      e.dataTransfer.setData('text/plain', JSON.stringify({
                        type: 'person',
                        personId: person.id,
                        taskId: selectedTaskForPeopleFilter.id
                      }));
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {/* Avatar - clickable */}
                    <div
                      className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPerson({
                          ...person,
                          fullName,
                          initials,
                          avatarColor,
                          utilization
                        });
                      }}
                    >
                      {person.profile_picture ? (
                        <img
                          src={person.profile_picture}
                          alt={fullName}
                          className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-semibold shadow-sm`}>
                          {initials}
                        </div>
                      )}
                    </div>

                    {/* Name and info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-900 truncate">
                        {person.first_name && person.last_name
                          ? `${person.last_name} ${person.first_name}`
                          : person.username}
                      </div>
                      {selectedTaskForPeopleFilter && (
                        <div className="text-[10px] text-brand-600">
                          {selectedTaskForPeopleFilter.startTime}-{selectedTaskForPeopleFilter.endTime}
                        </div>
                      )}
                    </div>

                    {/* Utilization */}
                    {!selectedTaskForPeopleFilter && (
                      <div className="flex-shrink-0">
                        <div className={`text-xs font-semibold ${
                          utilization === 100 ? 'text-green-700' :
                          utilization >= 76 ? 'text-red-700' :
                          utilization >= 51 ? 'text-orange-700' :
                          utilization >= 26 ? 'text-yellow-700' :
                          'text-slate-500'
                        }`}>
                          {utilization}%
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Utilization bar */}
                  {!selectedTaskForPeopleFilter && (
                    <div className="mt-2 w-full bg-steel-200 rounded-full h-1">
                      <div
                        className={`h-1 rounded-full transition-all ${
                          utilization === 100 ? 'bg-green-600' :
                          utilization >= 76 ? 'bg-red-500' :
                          utilization >= 51 ? 'bg-orange-500' :
                          utilization >= 26 ? 'bg-yellow-500' :
                          'bg-steel-400'
                        }`}
                        style={{ width: `${utilization}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          );
        })()}
        </div>
      )}

      {/* Avatar Preview Modal - Experience & Stats */}
      {selectedPerson && (() => {
        // Calculate person's experience from ALL project assignments (across all projects)
        const allAssignmentsFlat = Object.values(allProjectAssignments).flat();
        const personAssignments = allAssignmentsFlat.filter(a =>
          a.assignedPeople && a.assignedPeople.includes(selectedPerson.id)
        );

        // Get today's schedule for this person
        const todayStr = format(selectedDate, 'yyyy-MM-dd');
        const todaySchedule = personAssignments
          .filter(a => a.date === todayStr)
          .map(a => {
            // Get individual times if available
            const individualTimes = a.individual_times?.[selectedPerson.id];
            const startTime = individualTimes?.start_time || a.startTime;
            const endTime = individualTimes?.end_time || a.endTime;
            return {
              id: a.id,
              turbineId: a.turbineId,
              category: a.category,
              subcategory: a.subcategory,
              startTime,
              endTime,
              categoryColor: a.categoryColor || '#6366f1'
            };
          })
          .sort((a, b) => a.startTime.localeCompare(b.startTime));

        // Debug: log assignments to see turbineModel values
        console.log('Person assignments:', personAssignments.map(a => ({
          id: a.id,
          subcategory: a.subcategory,
          turbineModel: a.turbineModel,
          powerRating: a.powerRating,
          projectId: a.projectId
        })));

        // Group by turbine model - each assignment now has turbineModel and powerRating
        const modelExperience = {};

        personAssignments.forEach(entry => {
          // Use model info embedded in assignment (from loadAllProjectAssignments)
          const modelKey = entry.turbineModel || 'Unknown Model';
          const powerRating = entry.powerRating || '';
          const turbineType = entry.turbineType || '';

          if (entry.startTime && entry.endTime) {
            const [startH, startM] = entry.startTime.split(':').map(Number);
            const [endH, endM] = entry.endTime.split(':').map(Number);
            const minutes = (endH * 60 + endM) - (startH * 60 + startM);

            if (!modelExperience[modelKey]) {
              modelExperience[modelKey] = {
                model: modelKey,
                powerRating: powerRating,
                turbineType: turbineType,
                minutes: 0,
                taskCount: 0,
                tasks: {}
              };
            }
            modelExperience[modelKey].minutes += minutes;
            modelExperience[modelKey].taskCount++;

            // Track tasks per model
            const taskKey = entry.subcategory || 'Unknown';
            if (!modelExperience[modelKey].tasks[taskKey]) {
              modelExperience[modelKey].tasks[taskKey] = {
                task: taskKey,
                category: entry.category || 'Uncategorized',
                color: entry.categoryColor || '#6B7280',
                minutes: 0,
                count: 0
              };
            }
            modelExperience[modelKey].tasks[taskKey].minutes += minutes;
            modelExperience[modelKey].tasks[taskKey].count++;
          }
        });

        // Convert modelExperience to sorted list with formatted hours
        const modelList = Object.values(modelExperience)
          .map(m => ({
            ...m,
            hours: Math.floor(m.minutes / 60),
            remainingMins: m.minutes % 60,
            taskList: Object.values(m.tasks)
              .map(t => ({
                ...t,
                hours: Math.floor(t.minutes / 60),
                remainingMins: t.minutes % 60
              }))
              .sort((a, b) => b.minutes - a.minutes)
          }))
          .sort((a, b) => b.minutes - a.minutes);

        // Debug: log modelList
        console.log('modelList:', modelList);
        console.log('selectedModel:', selectedModel);

        // Get experience list based on selected model
        let experienceList = [];
        let totalMinutes = 0;
        let totalTaskCount = 0;

        if (selectedModel) {
          // Find the selected model in modelList
          const selectedModelData = modelList.find(m => m.model === selectedModel);
          console.log('selectedModelData:', selectedModelData);
          if (selectedModelData) {
            experienceList = selectedModelData.taskList;
            totalMinutes = selectedModelData.minutes;
            totalTaskCount = selectedModelData.taskCount;
          }
        } else {
          // All models - aggregate all tasks across models
          const allTasksMap = {};
          modelList.forEach(m => {
            m.taskList.forEach(t => {
              if (!allTasksMap[t.task]) {
                allTasksMap[t.task] = {
                  task: t.task,
                  category: t.category,
                  color: t.color,
                  minutes: 0,
                  count: 0
                };
              }
              allTasksMap[t.task].minutes += t.minutes;
              allTasksMap[t.task].count += t.count;
            });
          });
          experienceList = Object.values(allTasksMap)
            .map(t => ({
              ...t,
              hours: Math.floor(t.minutes / 60),
              remainingMins: t.minutes % 60
            }))
            .sort((a, b) => b.minutes - a.minutes);

          totalMinutes = modelList.reduce((acc, m) => acc + m.minutes, 0);
          totalTaskCount = personAssignments.length;
        }

        const totalHours = Math.floor(totalMinutes / 60);
        const totalRemainingMins = totalMinutes % 60;

        // Debug: final experienceList
        console.log('experienceList:', experienceList);
        console.log('experienceList.length:', experienceList.length);

        return ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/50"
            style={{ zIndex: 99999 }}
            onClick={() => { setSelectedPerson(null); setSelectedModel(null); }}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-4 text-white">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  {selectedPerson.profile_picture ? (
                    <img
                      src={selectedPerson.profile_picture}
                      alt={selectedPerson.fullName}
                      className="w-14 h-14 rounded-full object-cover border-2 border-white/30 shadow-lg"
                    />
                  ) : (
                    <div className={`w-14 h-14 rounded-full ${selectedPerson.avatarColor} flex items-center justify-center text-white text-lg font-bold shadow-lg border-2 border-white/30`}>
                      {selectedPerson.initials}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{selectedPerson.fullName}</h3>
                    {selectedPerson.email && (
                      <p className="text-sm text-white/70">{selectedPerson.email}</p>
                    )}
                  </div>
                  <button
                    onClick={() => { setSelectedPerson(null); setSelectedModel(null); }}
                    className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="flex flex-1 overflow-hidden">
                {/* Left Column - Info & Models */}
                <div className="w-1/2 border-r border-steel-200 flex flex-col overflow-y-auto">
                  {/* Turbine Models Section */}
                  {modelList.length > 0 && (
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="text-xs font-medium text-slate-500 mb-2">Turbine Models Experience</div>
                      <div className="flex flex-wrap gap-2">
                        {/* All button */}
                        <button
                          onClick={() => setSelectedModel(null)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                            !selectedModel
                              ? 'bg-slate-700 text-white shadow-md'
                              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                          }`}
                        >
                          All Models
                        </button>
                        {modelList.map((m, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedModel(selectedModel === m.model ? null : m.model)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                              selectedModel === m.model
                                ? 'bg-brand-600 text-white shadow-md'
                                : 'bg-white text-slate-700 border border-slate-200 hover:bg-brand-50 hover:border-brand-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{m.model}</span>
                              {m.powerRating && (
                                <span className={`text-[10px] ${selectedModel === m.model ? 'text-brand-200' : 'text-slate-400'}`}>
                                  {m.powerRating} MW
                                </span>
                              )}
                            </div>
                            <div className={`text-[10px] ${selectedModel === m.model ? 'text-brand-200' : 'text-slate-400'}`}>
                              {m.hours}h {m.remainingMins > 0 ? `${m.remainingMins}m` : ''} • {m.taskCount} tasks
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stats Summary */}
                  <div className="grid grid-cols-3 gap-3 p-4 bg-steel-50 border-b border-steel-200">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-slate-900">
                        {totalHours}h {totalRemainingMins > 0 ? `${totalRemainingMins}m` : ''}
                      </div>
                      <div className="text-xs text-slate-500">
                        {selectedModel ? `Hours on ${selectedModel}` : 'Total Hours'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-slate-900">{totalTaskCount}</div>
                      <div className="text-xs text-slate-500">Tasks Done</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-slate-900">{experienceList.length}</div>
                      <div className="text-xs text-slate-500">Task Types</div>
                    </div>
                  </div>

                  {/* Today's Schedule */}
                  <div className="px-4 py-3 border-b border-steel-200 flex-1 overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-slate-700">
                        Today's Schedule
                        <span className="ml-2 text-xs font-normal text-slate-400">
                          ({format(selectedDate, 'dd MMM yyyy')})
                        </span>
                      </span>
                      {selectedPerson.utilization !== undefined && (
                        <span className={`text-sm font-semibold ${
                          selectedPerson.utilization === 100 ? 'text-green-700' :
                          selectedPerson.utilization >= 76 ? 'text-red-700' :
                          selectedPerson.utilization >= 51 ? 'text-orange-700' :
                          selectedPerson.utilization >= 26 ? 'text-yellow-700' :
                          'text-slate-500'
                        }`}>
                          {selectedPerson.utilization}% utilized
                        </span>
                      )}
                    </div>

                    {/* Utilization bar */}
                    {selectedPerson.utilization !== undefined && (
                      <div className="w-full bg-steel-200 rounded-full h-1.5 mb-3">
                        <div
                          className={`h-1.5 rounded-full transition-all ${
                            selectedPerson.utilization === 100 ? 'bg-green-600' :
                            selectedPerson.utilization >= 76 ? 'bg-red-500' :
                            selectedPerson.utilization >= 51 ? 'bg-orange-500' :
                            selectedPerson.utilization >= 26 ? 'bg-yellow-500' :
                            'bg-steel-400'
                          }`}
                          style={{ width: `${selectedPerson.utilization}%` }}
                        />
                      </div>
                    )}

                    {/* Schedule Table */}
                    {todaySchedule.length === 0 ? (
                      <div className="text-center py-4 text-slate-400 text-sm">
                        No tasks scheduled for today
                      </div>
                    ) : (
                      <div className="overflow-y-auto flex-1 -mx-1">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-white">
                            <tr className="text-xs text-slate-500 uppercase">
                              <th className="text-left py-1.5 px-2 font-medium">Time</th>
                              <th className="text-left py-1.5 px-2 font-medium">Turbine</th>
                              <th className="text-left py-1.5 px-2 font-medium">Task</th>
                            </tr>
                          </thead>
                          <tbody>
                            {todaySchedule.map((task, index) => (
                              <tr key={task.id || index} className="border-t border-steel-100 hover:bg-steel-50">
                                <td className="py-2 px-2 whitespace-nowrap font-medium text-slate-700">
                                  {task.startTime} - {task.endTime}
                                </td>
                                <td className="py-2 px-2 whitespace-nowrap font-semibold text-slate-900">
                                  T{task.turbineId}
                                </td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: task.categoryColor }}
                                    />
                                    <span className="text-slate-600 truncate" title={task.subcategory}>
                                      {task.subcategory}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Total hours for today */}
                        <div className="mt-2 pt-2 border-t border-steel-200 px-2 text-sm text-slate-600">
                          <span className="font-medium">Total: </span>
                          {(() => {
                            const totalMins = todaySchedule.reduce((acc, t) => {
                              const [sh, sm] = t.startTime.split(':').map(Number);
                              const [eh, em] = t.endTime.split(':').map(Number);
                              return acc + (eh * 60 + em) - (sh * 60 + sm);
                            }, 0);
                            const hours = Math.floor(totalMins / 60);
                            const mins = totalMins % 60;
                            return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
                          })()}
                          <span className="text-slate-400 ml-2">({todaySchedule.length} tasks)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Experience Tasks */}
                <div className="w-1/2 flex flex-col bg-steel-50">
                  <div className="px-4 py-3 border-b border-steel-200 bg-white">
                    <h4 className="text-sm font-semibold text-slate-700">
                      {selectedModel ? `Tasks on ${selectedModel}` : 'Experience by Task'}
                      {experienceList.length > 0 && (
                        <span className="ml-2 text-xs font-normal text-slate-400">({experienceList.length})</span>
                      )}
                    </h4>
                  </div>
                  <div className="p-3">
                    {experienceList.length === 0 ? (
                      <div className="text-center py-6 text-slate-400 text-sm">
                        No task history
                      </div>
                    ) : (
                      <div className={`space-y-2 ${experienceList.length > 5 ? 'max-h-[280px] overflow-y-auto pr-1' : ''}`}>
                        {experienceList.map((task, index) => (
                          <div key={index} className="flex items-center gap-3 p-2 rounded-xl bg-white border border-steel-100 shadow-sm">
                            <div
                              className="w-2 h-8 rounded-full flex-shrink-0"
                              style={{ backgroundColor: task.color }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate">{task.task}</div>
                              <div className="text-xs text-slate-500">{task.category}</div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-sm font-semibold text-slate-900">
                                {task.hours}h {task.remainingMins > 0 ? `${task.remainingMins}m` : ''}
                              </div>
                              <div className="text-xs text-slate-500">{task.count}x</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default PeopleList;
